import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { useDashboard } from '@/context/DashboardContext'
import { loginPathReturningTo } from '@/lib/auth/returnPath'

import { CampLoader } from './CampLoader'

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { state } = useDashboard()
  const { pathname, search, hash } = useLocation()
  if (state.isLoading) {
    return <CampLoader />
  }
  // Signing in comes back here, so an upgrade link still reaches billing. Not once this tab's session has
  // ended: the next person to sign in on this browser shouldn't land on the last person's page.
  if (!state.isAuthenticated) {
    return <Navigate to={state.sessionEnded ? '/login' : loginPathReturningTo(`${pathname}${search}${hash}`)} replace />
  }
  return <>{children}</>
}

/**
 * A legacy route's redirect that keeps the incoming query: Stripe checkout
 * returns to `/billing?success=1`. The target's own parameters win.
 */
export function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation()
  const [pathname, query = ''] = to.split('?')
  const params = new URLSearchParams(query)
  new URLSearchParams(search).forEach((value, key) => {
    if (!params.has(key)) params.append(key, value)
  })
  const merged = params.toString()
  return <Navigate to={`${pathname}${merged ? `?${merged}` : ''}${hash}`} replace />
}
